/* ----------------------------------------------------------------------------
 * Executive OS live composition (Main dashboard) — PURE, dependency-free helpers
 * that turn real Sales + SEO signals into the Business Health composite + the AI
 * Executive Summary with HONEST states. No network / no api import, so it is
 * unit-testable in isolation (node:test). Consumed by data/sources.ts.
 * -------------------------------------------------------------------------- */
import { formatCurrency } from '@/lib/utils';
import type { Confidence, Evidence } from '@/types';
import type { SeoExecSnapshot } from '@/types/seod';

/** Structural subset of the sending-status snapshot the composite needs — kept
 *  local so this module has no dependency on the api-coupled sources.ts DTO. */
export interface SendingSignal {
  paused: boolean;
  bounce_rate: number;
  complaint_rate: number;
  thresholds?: { bounce_warn?: number; complaint_warn?: number };
}

/** Real Sales + SEO signals feeding the Business Health composite + the AI
 *  Executive Summary. Every field is nullable — a null factor is simply left
 *  OUT (never scored as zero), so the composite reflects only what truly has
 *  data. Workspace-wide (the Exec OS is a workspace surface). */
export interface ExecSignals {
  leads: number | null;
  replies: number | null;
  openPipelinePence: number | null;
  freshOpps: number | null;
  sending: SendingSignal | null;
  seo: SeoExecSnapshot | null;
}

interface HealthFactor {
  label: string;
  weight: number;
  subScore: number;
  detail: string;
  source: string;
}

function clampScore(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Compute the LIVE Business Health composite + its Evidence from the real
 *  signals. Deterministic weighted mean of ONLY the factors that have data;
 *  weights are renormalised across present factors for the evidence bars. Every
 *  factor is source-attributed. Returns null ONLY when NO factor has data — an
 *  honest "calibrating", never an invented score. */
export function computeBusinessHealth(sig: ExecSignals, observedAt: string): { score: number; evidence: Evidence } | null {
  const factors: HealthFactor[] = [];

  // 1. Sales pipeline momentum — presence + freshness of open pipeline value.
  if (sig.openPipelinePence !== null) {
    const gbp = sig.openPipelinePence / 100;
    const hasPipe = gbp > 0;
    const fresh = sig.freshOpps ?? 0;
    const sub = clampScore((hasPipe ? 68 : 38) - Math.min(fresh, 5) * 3, 0, 92);
    factors.push({
      label: 'Sales pipeline momentum',
      weight: 0.28,
      subScore: sub,
      detail: hasPipe
        ? `${formatCurrency(gbp)} open pipeline${fresh > 0 ? ` · ${fresh} awaiting first touch` : ''}`
        : 'No open pipeline value yet',
      source: 'Sales Engine · /crm/opportunities',
    });
  }

  // 2. Outreach efficiency — reply rate over discovered leads.
  if (sig.leads !== null && sig.leads > 0 && sig.replies !== null) {
    const rate = sig.replies / sig.leads;
    factors.push({
      label: 'Outreach efficiency',
      weight: 0.24,
      subScore: clampScore(rate * 600, 0, 100), // ~16.7% reply rate -> 100
      detail: `Reply rate ${(rate * 100).toFixed(1)}% (${sig.replies} of ${sig.leads} leads)`,
      source: 'Sales Engine · /summary',
    });
  }

  // 3. Deliverability health — bounce/complaint vs thresholds; paused = low.
  if (sig.sending) {
    const s = sig.sending;
    const bounceWarn = s.thresholds?.bounce_warn ?? 0.02;
    const complaintWarn = s.thresholds?.complaint_warn ?? 0.0005;
    let sub = 90;
    if (s.bounce_rate > bounceWarn) sub -= 30;
    if (s.complaint_rate > complaintWarn) sub -= 30;
    if (s.paused) sub = Math.min(sub, 25);
    factors.push({
      label: 'Deliverability health',
      weight: 0.2,
      subScore: clampScore(sub, 0, 95),
      detail: s.paused
        ? 'Sending is paused'
        : `Bounce ${(s.bounce_rate * 100).toFixed(1)}% · complaint ${(s.complaint_rate * 100).toFixed(2)}%`,
      source: 'Sales Engine · /sending-status',
    });
  }

  // 4. SEO visibility — page-one share of ranked organic + net 30d movement.
  if (sig.seo && sig.seo.organicState === 'ok' && sig.seo.totalOrganicKeywords > 0) {
    const seo = sig.seo;
    const ratio = seo.pageOneKeywords / seo.totalOrganicKeywords;
    let sub = clampScore(40 + ratio * 200, 0, 95);
    const movementReal = seo.movementState === 'ok' || seo.movementState === 'workspace';
    if (movementReal) {
      sub = clampScore(sub + Math.min(seo.gains, 5) * 2 - Math.min(seo.losses, 5) * 3, 0, 97);
    }
    factors.push({
      label: 'SEO visibility',
      weight: 0.28,
      subScore: sub,
      detail: `${seo.pageOneKeywords} of ${seo.totalOrganicKeywords} organic keywords on page one${movementReal ? ` · ${seo.gains} gains / ${seo.losses} losses (30d)` : ''}`,
      source: 'SEO Engine · ranked-organic selector',
    });
  }

  if (factors.length === 0) return null;

  const weightSum = factors.reduce((a, f) => a + f.weight, 0);
  const score = Math.round(factors.reduce((a, f) => a + f.subScore * f.weight, 0) / weightSum);
  const confidence: Confidence = factors.length >= 3 ? 'high' : factors.length === 2 ? 'medium' : 'low';
  return {
    score,
    evidence: {
      method: 'Business Health — live weighted cross-engine composite',
      confidence,
      version: 'v1-live',
      updated_at: observedAt,
      factors: factors.map((f) => ({
        label: f.label,
        weight: f.weight / weightSum,
        detail: f.detail,
        source: f.source,
        observed_at: observedAt,
      })),
    },
  };
}

/** Compose the AI Executive Summary from the SAME real signals — a factual,
 *  advisory roll-up (never an invented narrative). Only mentions figures that
 *  exist; honest states for SEO movement/alerts. Null ONLY when nothing has
 *  data. */
export function composeExecSummary(sig: ExecSignals): string | null {
  const parts: string[] = [];

  if (sig.leads !== null && sig.leads > 0) {
    const rep = sig.replies ?? 0;
    parts.push(`${sig.leads} lead${sig.leads === 1 ? '' : 's'} discovered with ${rep} repl${rep === 1 ? 'y' : 'ies'}.`);
  }
  if (sig.openPipelinePence !== null && sig.openPipelinePence > 0) {
    const fresh = sig.freshOpps ?? 0;
    parts.push(`${formatCurrency(sig.openPipelinePence / 100)} of open pipeline${fresh > 0 ? `, ${fresh} awaiting a first touch` : ''}.`);
  }

  const seo = sig.seo;
  if (seo && seo.organicState === 'ok' && seo.totalOrganicKeywords > 0) {
    parts.push(
      `SEO: ${seo.totalOrganicKeywords} organic keyword${seo.totalOrganicKeywords === 1 ? '' : 's'}, ${seo.pageOneKeywords} on page one and ${seo.strikingDistanceKeywords} in striking distance.`,
    );
    if (seo.movementState === 'ok' || seo.movementState === 'workspace') {
      parts.push(`${seo.gains} ranking gain${seo.gains === 1 ? '' : 's'} and ${seo.losses} loss${seo.losses === 1 ? '' : 'es'} in the last 30 days.`);
    } else if (seo.movementState === 'insufficient_history') {
      parts.push('Not enough ranking history yet to report gains and losses.');
    }
    if (seo.alertsState === 'ok' && seo.openAlerts > 0) {
      parts.push(`${seo.openAlerts} open SEO alert${seo.openAlerts === 1 ? '' : 's'} to review.`);
    }
    const move = seo.topActions.find((a) => a.action);
    if (move) parts.push(`Highest-leverage SEO move: ${move.action}`);
  }

  if (sig.sending) {
    const s = sig.sending;
    const bounceWarn = s.thresholds?.bounce_warn ?? 0.02;
    if (s.paused) parts.push('Outreach sending is currently paused — resume is owner-only.');
    else if (s.bounce_rate > bounceWarn) parts.push(`Watch deliverability: bounce rate ${(s.bounce_rate * 100).toFixed(1)}%.`);
  }

  return parts.length === 0 ? null : parts.join(' ');
}
